const METERS_PER_DEGREE_LAT = 6_371_000 * Math.PI / 180
const ARC_STEPS = 32

type LngLat = [lng: number, lat: number]
export type LatLngTuple = [lat: number, lng: number]

/**
 * Outline of the area two stop zones share, as [lat, lng] for a leaflet Polygon. Drawn on a flat
 * plane around the first centre, which is exact enough at a few hundred metres. When one zone
 * lies wholly inside the other, the shared area is the smaller zone; with no overlap it is empty.
 */
export const buildZonesOverlapSingleVoyage = (
	[lngA, latA]: LngLat,
	radiusA: number,
	[lngB, latB]: LngLat,
	radiusB: number,
): LatLngTuple[] => {
	const metersPerDegreeLng = METERS_PER_DEGREE_LAT * Math.cos(latA * Math.PI / 180)
	const toLatLng = (x: number, y: number): LatLngTuple => [latA + y / METERS_PER_DEGREE_LAT, lngA + x / metersPerDegreeLng]
	const arc = (cx: number, cy: number, radius: number, from: number, to: number): LatLngTuple[] => {
		return Array.from({length: ARC_STEPS + 1}, (_, i) => {
			const angle = from + (to - from) * i / ARC_STEPS
			return toLatLng(cx + radius * Math.cos(angle), cy + radius * Math.sin(angle))
		})
	}

	const bx = (lngB - lngA) * metersPerDegreeLng
	const by = (latB - latA) * METERS_PER_DEGREE_LAT
	const distance = Math.hypot(bx, by)

	if (distance >= radiusA + radiusB) {
		return []
	}
	if (distance <= Math.abs(radiusA - radiusB)) {
		return radiusA < radiusB
			? arc(0, 0, radiusA, 0, 2 * Math.PI)
			: arc(bx, by, radiusB, 0, 2 * Math.PI)
	}

	const towardsB = Math.atan2(by, bx)
	// Half-angles of the arcs each zone has inside the other, from the law of cosines
	const alpha = Math.acos((distance ** 2 + radiusA ** 2 - radiusB ** 2) / (2 * distance * radiusA))
	const beta = Math.acos((distance ** 2 + radiusB ** 2 - radiusA ** 2) / (2 * distance * radiusB))

	return [
		...arc(0, 0, radiusA, towardsB - alpha, towardsB + alpha),
		...arc(bx, by, radiusB, towardsB + Math.PI - beta, towardsB + Math.PI + beta),
	]
}

/** South-west and north-east corners that fit both zones, for map.fitBounds. */
export const buildZonesBoundsSingleVoyage = (
	zones: { centre: LngLat, radius: number }[],
): [LatLngTuple, LatLngTuple] => {
	const corners = zones.flatMap(({centre: [lng, lat], radius}) => {
		const dLat = radius / METERS_PER_DEGREE_LAT
		const dLng = radius / (METERS_PER_DEGREE_LAT * Math.cos(lat * Math.PI / 180))
		return [[lat - dLat, lng - dLng], [lat + dLat, lng + dLng]]
	})
	return [
		[Math.min(...corners.map(([lat]) => lat)), Math.min(...corners.map(([, lng]) => lng))],
		[Math.max(...corners.map(([lat]) => lat)), Math.max(...corners.map(([, lng]) => lng))],
	]
}
